import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, CheckCircle2, Camera, X } from "lucide-react";
import { buildingsApi, doorsApi, clientPortalApi } from "@/services/api";
import { apiError } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function PortalReportFailure() {
  const navigate = useNavigate();
  const [buildings, setBuildings] = useState([]);
  const [doors, setDoors] = useState([]);
  const [buildingId, setBuildingId] = useState("");
  const [doorId, setDoorId] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  const [photo, setPhoto] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { buildingsApi.getAll().then((r) => setBuildings(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setDoorId("");
    if (buildingId) doorsApi.getAll(buildingId).then((r) => setDoors(r.data || [])).catch(() => setDoors([]));
    else setDoors([]);
  }, [buildingId]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(String(reader.result || ""));
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (!doorId) { toast.error("Please choose the affected door."); return; }
    if (!description.trim()) { toast.error("Please describe the problem."); return; }
    setSaving(true);
    try {
      await clientPortalApi.reportDoorFailure({ door_id: doorId, description: description.trim(), photo_base64: photo || undefined, priority });
      setDone(true);
      toast.success("Reported — the FD Check team has been notified");
    } catch (e) { toast.error(apiError(e, "Could not submit report")); }
    finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg py-10 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-emerald-500" />
        <h2 className="text-xl font-bold text-slate-800">Fault reported</h2>
        <p className="mt-1 text-sm text-slate-500">Our team will raise a job and be in touch. Thank you.</p>
        <div className="mt-5 flex justify-center gap-2">
          <Button variant="outline" onClick={() => { setDone(false); setDescription(""); setPhoto(""); setDoorId(""); }} data-testid="report-another">Report another</Button>
          <Button onClick={() => navigate("/portal")} data-testid="report-home">Back to home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg space-y-6">
      <PageHeader title="Report a fire door fault" subtitle="Flag a problem with a door for the FD Check team" testId="report-failure-header" />
      <Card className="space-y-4 p-5">
        <div className="space-y-1.5">
          <Label>Building</Label>
          <Select value={buildingId} onValueChange={setBuildingId}>
            <SelectTrigger data-testid="report-building"><SelectValue placeholder="Select a building" /></SelectTrigger>
            <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Door</Label>
          <Select value={doorId} onValueChange={setDoorId} disabled={!buildingId}>
            <SelectTrigger data-testid="report-door"><SelectValue placeholder={buildingId ? "Select the affected door" : "Pick a building first"} /></SelectTrigger>
            <SelectContent>{doors.map((d) => <SelectItem key={d.id} value={d.id}>{d.door_reference}{d.location ? ` · ${d.location}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>What is wrong?</Label>
          <Textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. door won't self-close, damaged seal, propped open…" data-testid="report-description" />
        </div>
        <div className="space-y-1.5">
          <Label>Urgency</Label>
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger data-testid="report-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="emergency">Emergency</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Photo (optional)</Label>
          {photo ? (
            <div className="relative inline-block">
              <img src={photo} alt="fault" className="h-24 w-24 rounded-lg object-cover" />
              <button onClick={() => setPhoto("")} className="absolute -right-2 -top-2 rounded-full bg-red-500 p-1 text-white" data-testid="report-photo-remove"><X className="h-3 w-3" /></button>
            </div>
          ) : (
            <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-2 text-sm text-slate-500 hover:bg-slate-50">
              <Camera className="h-4 w-4" /> Add photo
              <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="report-photo-input" />
            </label>
          )}
        </div>
        <Button onClick={submit} disabled={saving} className="w-full" data-testid="report-submit">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <AlertTriangle className="mr-2 h-4 w-4" />} Submit report
        </Button>
      </Card>
    </div>
  );
}
